import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Heart, ArrowRight, LogOut, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { api, logout, requireMember } from "@/lib/auth";
import { VENDORS } from "@/lib/vendorData";
import { devLog } from "@/lib/log";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* Resolve a slug to {name, blurb, image, href} from either the legacy
 * VENDORS map or the live `/api/vendors` listing. */
const resolveVendor = (slug, dbVendors) => {
  if (VENDORS[slug]) {
    const v = VENDORS[slug];
    return {
      name: v.name,
      blurb: v.tagline,
      image: v.image || v.portrait || "",
      href: `/vendors/${slug}`,
    };
  }
  const db = dbVendors.find((v) => v.slug === slug);
  if (db) {
    return {
      name: db.company_name || slug,
      blurb: db.tagline || "",
      image: (db.media_urls && db.media_urls[0]) || "",
      href: `/vendors/${slug}`,
    };
  }
  return { name: slug, blurb: "", image: "", href: `/vendors/${slug}` };
};

const MyFavourites = () => {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [slugs, setSlugs] = useState([]);
  const [dbVendors, setDbVendors] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { user, redirect } = await requireMember();
      if (cancelled) return;
      if (!user) {
        navigate("/signin?next=/my-favourites", { replace: true });
        return;
      }
      if (redirect) {
        navigate(redirect, { replace: true });
        return;
      }
      setMe(user);
      try {
        const [favRes, vendorsRes] = await Promise.all([
          axios.get(`${API}/favorites`, { params: { email: user.email } }),
          axios.get(`${API}/vendors`),
        ]);
        if (cancelled) return;
        setSlugs(favRes.data.slugs || []);
        setDbVendors(vendorsRes.data || []);
      } catch (e) {
        devLog("ld_myfavs load failed:", e?.message);
        if (!cancelled) toast("Couldn't load favourites", { duration: 2400 });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const handleRemove = async (slug) => {
    try {
      await axios.post(`${API}/favorites/toggle`, {
        email: me.email,
        vendor_slug: slug,
      });
      setSlugs((s) => s.filter((x) => x !== slug));
      toast("Removed from favourites", { duration: 1800 });
    } catch (e) {
      devLog("ld_myfavs remove failed:", e?.message);
      toast("Couldn't update favourite", { duration: 2200 });
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  return (
    <main
      data-testid="my-favourites-page"
      className="min-h-[100dvh] px-6 md:px-12 py-12 md:py-20"
      style={{ backgroundColor: "var(--ld-ivory)" }}
    >
      <div className="max-w-5xl mx-auto">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <Link
              to="/"
              className="font-accent text-[10px] tracking-[0.25em] uppercase text-[var(--ld-gold-deep)] hover:text-[var(--ld-ink)]"
            >
              ← Luxé Directory
            </Link>
            <h1 className="font-serif-display mt-3 text-3xl sm:text-5xl text-[var(--ld-ink)] font-medium leading-tight">
              My Favourites
            </h1>
            {me && (
              <p className="mt-3 text-sm text-[var(--ld-ink-muted)]">
                Signed in as{" "}
                <span className="text-[var(--ld-ink)] font-medium">{me.email}</span>
              </p>
            )}
          </div>
          <button
            data-testid="my-favourites-logout"
            onClick={handleLogout}
            className="inline-flex items-center gap-2 text-[11px] tracking-[0.18em] uppercase text-[var(--ld-ink-muted)] hover:text-[var(--ld-ink)] transition-colors"
          >
            <LogOut size={13} strokeWidth={1.6} />
            Sign out
          </button>
        </div>

        <div className="mt-12">
          {loading && (
            <div className="flex items-center gap-3 text-[var(--ld-ink-muted)]">
              <Loader2 size={16} className="animate-spin" />
              <span>Loading favourites…</span>
            </div>
          )}

          {!loading && slugs.length === 0 && (
            <div
              data-testid="my-favourites-empty"
              className="text-center border border-[var(--ld-border)] p-12 max-w-xl mx-auto bg-[var(--ld-ivory-warm)]"
            >
              <Heart
                size={28}
                strokeWidth={1.4}
                className="mx-auto text-[var(--ld-gold-deep)]"
              />
              <h2 className="font-serif-display text-2xl text-[var(--ld-ink)] mt-4">
                No favourites yet.
              </h2>
              <p className="mt-3 text-sm text-[var(--ld-ink-muted)]">
                Browse the vendor directory and tap the heart on any reel to save
                it here for later.
              </p>
              <Link
                to="/vendor-listing"
                className="inline-flex items-center gap-2 mt-6 text-[11px] tracking-[0.22em] uppercase bg-[var(--ld-ink)] text-[var(--ld-gold)] px-5 py-3 hover:opacity-90"
              >
                Browse vendors
                <ArrowRight size={13} strokeWidth={1.6} />
              </Link>
            </div>
          )}

          {!loading && slugs.length > 0 && (
            <ul
              data-testid="my-favourites-list"
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
            >
              {slugs.map((slug) => {
                const v = resolveVendor(slug, dbVendors);
                return (
                  <li
                    key={slug}
                    data-testid={`my-favourite-${slug}`}
                    className="relative bg-white border border-[var(--ld-border)] overflow-hidden group"
                  >
                    {v.image ? (
                      <Link to={v.href} className="block aspect-[4/5] overflow-hidden">
                        <img
                          src={v.image}
                          alt={v.name}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                        />
                      </Link>
                    ) : (
                      <div className="aspect-[4/5] bg-[var(--ld-ink)]/5 flex items-center justify-center text-[var(--ld-ink-muted)] text-sm">
                        No image
                      </div>
                    )}
                    <div className="p-4">
                      <h3 className="font-serif-display text-[20px] text-[var(--ld-ink)] leading-tight">
                        {v.name}
                      </h3>
                      {v.blurb && (
                        <p className="mt-1 text-[12px] text-[var(--ld-ink-muted)] line-clamp-2">
                          {v.blurb}
                        </p>
                      )}
                      <div className="mt-4 flex items-center justify-between">
                        <Link
                          to={v.href}
                          className="text-[11px] tracking-[0.18em] uppercase text-[var(--ld-ink)] underline underline-offset-4 decoration-[var(--ld-gold-deep)] hover:decoration-[var(--ld-ink)]"
                        >
                          View profile
                        </Link>
                        <button
                          data-testid={`my-favourite-remove-${slug}`}
                          onClick={() => handleRemove(slug)}
                          aria-label="Remove favourite"
                          className="inline-flex items-center justify-center h-8 w-8 text-rose-500 hover:text-rose-700 transition-colors"
                        >
                          <Heart size={15} strokeWidth={1.8} fill="currentColor" />
                        </button>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </main>
  );
};

export default MyFavourites;
